import React, { useState } from 'react';

import { Icon, PopupMenu } from 'design-system-web';
import cx from 'classnames';
import { Button } from 'components';
import { useTranslation } from 'react-i18next';

import {
  AddItemType,
  AddProductToChargeDetails,
  Payer,
} from 'lane-shared/domains/billingPayments/types';
import { type SupportedPaymentCurrency } from 'domains-billing-currency';

import { AddProductModalForm } from 'pages/portal/admin/channel/charges-invoices/add-product-to-charge/AddProductForm/AddProductModalForm';
import { AddedProductsTable } from 'pages/portal/admin/channel/charges-invoices/add-product-to-charge/AddedProductsTable/AddedProductsTable';
import { AddedProductsTotal } from 'pages/portal/admin/channel/charges-invoices/add-product-to-charge/AddedProductsTotal';

import { Modal } from 'components/lds';
import { H4 } from 'components/typography';

import styles from './addProductToChargeStyles.scss';

interface AddProductToChargeProps {
  listOfProducts: AddProductToChargeDetails[];
  setListOfProducts: React.Dispatch<
    React.SetStateAction<AddProductToChargeDetails[]>
  >;
  currency: SupportedPaymentCurrency;
  payer: Payer;
  inclusivePricing?: boolean;
  className?: string;
  showTotals?: boolean;
}

export function AddProductToCharge({
  listOfProducts,
  setListOfProducts,
  currency,
  payer,
  inclusivePricing = false,
  className,
  showTotals = true,
}: AddProductToChargeProps) {
  const { t } = useTranslation();

  const [isAddProductModalOpen, setIsAddProductModalOpen] = useState(false);
  const [formType, setFormType] = useState<AddItemType | undefined>(undefined);
  const [productToEditDetails, setProductToEditDetails] = useState<
    AddProductToChargeDetails | undefined
  >(undefined);

  const closeModal = () => {
    setIsAddProductModalOpen(false);
  };

  const openModal = () => {
    setIsAddProductModalOpen(true);
  };

  const actionButtonDropdownItems = [
    {
      label: t('abp.charges.addProduct.addItemButton.addExistingItem'),
      onSelect: () => {
        setFormType(AddItemType.EXISTING);
        openModal();
      },
    },
    {
      label: t('abp.charges.addProduct.addItemButton.addCustomItem'),
      onSelect: () => {
        setFormType(AddItemType.CUSTOM);
        openModal();
      },
    },
  ];

  const handleEditExistingProduct = (product: AddProductToChargeDetails) => {
    setProductToEditDetails(product);
    setFormType(product.tableProductType);
    openModal();
  };

  const handleRemoveExistingProduct = (product: AddProductToChargeDetails) => {
    setListOfProducts(prev =>
      prev.filter(
        existingProduct => existingProduct.tableRowId !== product.tableRowId
      )
    );
  };

  const addProduct = (product: AddProductToChargeDetails) => {
    setListOfProducts(prev => [...prev, product]);
  };

  const editProduct = async (updatedProduct: AddProductToChargeDetails) => {
    if (updatedProduct) {
      setListOfProducts(prev =>
        prev.map(existingProduct => {
          if (existingProduct.tableRowId === updatedProduct.tableRowId) {
            return updatedProduct;
          }

          return existingProduct;
        })
      );
    }

    setProductToEditDetails(undefined);
  };

  const isProductsPresent = listOfProducts.length > 0;

  return (
    <div className={cx(styles.addProductToChargeContainer, className)}>
      <H4 mb={5} className={styles.addProductsHeader}>
        {t('abp.charges.addProduct.header')}
      </H4>
      {isProductsPresent && (
        <>
          <AddedProductsTable
            listOfProducts={listOfProducts}
            currency={currency}
            editProduct={handleEditExistingProduct}
            removeProduct={handleRemoveExistingProduct}
          />
          {showTotals && (
            <AddedProductsTotal
              currency={currency}
              listOfProducts={listOfProducts}
            />
          )}
        </>
      )}
      <PopupMenu
        items={actionButtonDropdownItems}
        trigger={
          <Button
            endIcon={<Icon name="chevron-down" />}
            startIcon={<Icon name="plus" />}
            variant="contained"
            size="medium"
            className={cx(
              styles.addItemButton,
              !isProductsPresent && styles.addItemButtonNoProducts
            )}
            testId="addItemButton"
          >
            {t('abp.charges.addProduct.addItemButton')}
          </Button>
        }
      />
      <Modal
        isCloseButtonHidden
        isOpen={isAddProductModalOpen}
        onClose={() => closeModal()}
        testId="addProductModal"
        style={{ width: '50%', maxWidth: '50vw' }}
      >
        <AddProductModalForm
          formType={formType}
          addProduct={addProduct}
          editProduct={editProduct}
          closeModal={closeModal}
          currency={currency}
          productToEdit={productToEditDetails}
          inclusivePricing={inclusivePricing}
          payer={payer}
        />
      </Modal>
    </div>
  );
}
